"use client";

import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from "react";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { ChevronUp, Loader2 } from "lucide-react";
import { useStickToBottomContext } from "use-stick-to-bottom";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";
import { NoxusMessageContent } from "./NoxusMessageContent";
import { renderDataPart } from "./data-part-renderers";
import { ThinkingLine } from "./ThinkingLine";
import type { NoxusUIMessage } from "./noxus-message-types";
import type { useEarlierMessages } from "./useEarlierMessages";
import { highlightMessage, revealMessageAtom } from "./revealMessage";

const REPLY_OFFSET = "mt-4";

type MessageContentProps = ComponentProps<typeof NoxusMessageContent>;

type Earlier = Pick<
  ReturnType<typeof useEarlierMessages>,
  "hasEarlier" | "isLoading" | "fetchEarlier" | "prepend"
>;

// Memoized so typing in the composer, which re-renders the widget on every
// keystroke, does not re-render every message.
export const ChatMessageList = memo(function ChatMessageList({
  displayMessages,
  displayMessagesList,
  earlier,
  canLoadEarlier,
  assistantId,
  groupId,
  conversationId,
  regenerate,
  status,
  setSourcesDrawerOpen,
  publicMetadata,
  channelType,
  isGenie,
  isTurnUnstarted,
  waitLabel,
}: {
  displayMessages: { msg: NoxusUIMessage; originalIndex: number }[];
  displayMessagesList: NoxusUIMessage[];
  earlier: Earlier;
  canLoadEarlier: boolean;
  assistantId: string;
  groupId: string;
  conversationId: string;
  regenerate: MessageContentProps["regenerate"];
  status: MessageContentProps["status"];
  setSourcesDrawerOpen: MessageContentProps["setSourcesDrawerOpen"];
  publicMetadata: MessageContentProps["publicMetadata"];
  channelType: string | undefined;
  isGenie: boolean | undefined;
  isTurnUnstarted: boolean;
  waitLabel: string | null | undefined;
}) {
  const loadEarlier = useLoadEarlierKeepingPlace(
    earlier,
    displayMessagesList.length
  );
  const prefixes = useStablePrefixes(displayMessagesList);
  useRevealMessage({
    conversationId,
    messages: displayMessagesList,
    hasEarlier: earlier.hasEarlier,
    canLoad: canLoadEarlier,
    loadEarlier,
  });
  return (
    <>
      {earlier.hasEarlier && canLoadEarlier && (
        <LoadEarlierMessages
          onClick={() => void loadEarlier()}
          isLoading={earlier.isLoading}
        />
      )}
      {displayMessages.map(({ msg }, index) => {
        // Extract data-compaction parts and render them at the chat-list
        // level. The normal message renderer wraps parts in MessageContent
        // which has overflow-hidden, clipping any attempt to escape the 90%
        // width via negative margin or w-full.
        const { compactionParts, otherParts } = splitParts(msg);

        return (
          // Keyed by id alone: loading earlier messages shifts every index,
          // and an index in the key would remount the whole list.
          <React.Fragment key={`${msg.role}-${msg.id}`}>
            {compactionParts.map((part, ci) => (
              <div
                key={`compaction-${msg.id}-${ci}`}
                className="w-full"
                data-chat-message={msg.id}
              >
                {renderDataPart(
                  part,
                  `compaction-${msg.id}-${ci}`,
                  !!publicMetadata
                )}
              </div>
            ))}
            {otherParts.length > 0 && (
              <div
                data-chat-message={msg.id}
                className={cn(
                  isGenie ? "max-w-full min-w-full" : "max-w-[90%] min-w-[90%]",
                  msg.role === "user" && "self-end",
                  msg.role === "assistant" && REPLY_OFFSET,
                  msg.role === "assistant" &&
                    index === displayMessages.length - 1 &&
                    "min-h-32"
                )}
              >
                <NoxusMessageContent
                  agentId={assistantId}
                  workedMs={msg.metadata?.worked_ms}
                  parts={otherParts}
                  role={msg.role}
                  groupId={groupId}
                  conversationId={conversationId}
                  index={index}
                  messages={prefixes[index]}
                  regenerate={regenerate}
                  status={status}
                  id={msg.id}
                  dbMessageId={msg.metadata?.message_id}
                  setSourcesDrawerOpen={setSourcesDrawerOpen}
                  publicMetadata={publicMetadata}
                  senderRef={msg.metadata?.sender_ref ?? undefined}
                  channelType={channelType}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
      {isTurnUnstarted && (
        <div className={REPLY_OFFSET}>
          <ThinkingLine label={waitLabel ?? undefined} />
        </div>
      )}
    </>
  );
});

type SplitParts = {
  compactionParts: NoxusUIMessage["parts"];
  otherParts: NoxusUIMessage["parts"];
};

// Keyed by message: the chat SDK replaces only the message that is streaming,
// so a finished message keeps its parts arrays and its memoized render.
const splitPartsCache = new WeakMap<NoxusUIMessage, SplitParts>();

function splitParts(msg: NoxusUIMessage): SplitParts {
  let split = splitPartsCache.get(msg);
  if (!split) {
    split = {
      compactionParts: msg.parts.filter((p) => p.type === "data-compaction"),
      otherParts: msg.parts.filter((p) => p.type !== "data-compaction"),
    };
    splitPartsCache.set(msg, split);
  }
  return split;
}

// A message reads the list only up to the one after it. Each gets that prefix,
// the same array while those messages are unchanged, so a streamed token
// re-renders the streaming message and its predecessor, not the whole chat.
const prefixCache = new WeakMap<NoxusUIMessage, NoxusUIMessage[]>();

function stablePrefix(messages: NoxusUIMessage[], end: number) {
  const cached = prefixCache.get(messages[end - 1]);
  if (cached?.length === end && cached.every((m, i) => m === messages[i])) {
    return cached;
  }
  const prefix = messages.slice(0, end);
  prefixCache.set(messages[end - 1], prefix);
  return prefix;
}

function useStablePrefixes(messages: NoxusUIMessage[]) {
  return useMemo(
    () =>
      messages.map((_, index) =>
        stablePrefix(messages, Math.min(index + 2, messages.length))
      ),
    [messages]
  );
}

// Lives in the list, not the button: the last page removes the button in the
// same render that grows the list.
function useLoadEarlierKeepingPlace(earlier: Earlier, messageCount: number) {
  const { scrollRef, stopScroll } = useStickToBottomContext();
  const anchor = useRef<{ id: string; offset: number } | null>(null);
  const { fetchEarlier, prepend } = earlier;

  // Prepending grows the content above the viewport. Keep the message the
  // reader was looking at at the same height on screen.
  useLayoutEffect(() => {
    const viewport = scrollRef.current;
    const saved = anchor.current;
    if (!viewport || !saved) return;
    anchor.current = null;
    const el = messageElement(viewport, saved.id);
    if (el) viewport.scrollTop += offsetIn(viewport, el) - saved.offset;
  }, [messageCount, scrollRef]);

  return useCallback(async (): Promise<boolean> => {
    const page = await fetchEarlier();
    if (!page?.messages.length) return false;
    const viewport = scrollRef.current;
    const first = viewport?.querySelector<HTMLElement>("[data-chat-message]");
    if (viewport && first?.dataset.chatMessage) {
      anchor.current = {
        id: first.dataset.chatMessage,
        offset: offsetIn(viewport, first),
      };
    }
    if (!prepend(page)) {
      anchor.current = null;
      return false;
    }
    // Reading above: let go of the bottom, or the growing list is scrolled
    // back down to the newest message.
    stopScroll();
    return true;
  }, [fetchEarlier, prepend, scrollRef, stopScroll]);
}

// Bring a requested message on screen (see revealMessageAtom): load earlier
// pages until its row renders, then highlight it. Gives up when a page fails
// or the history runs out.
function useRevealMessage({
  conversationId,
  messages,
  hasEarlier,
  canLoad,
  loadEarlier,
}: {
  conversationId: string;
  messages: NoxusUIMessage[];
  hasEarlier: boolean;
  canLoad: boolean;
  loadEarlier: () => Promise<boolean>;
}) {
  const [reveal, setReveal] = useAtom(revealMessageAtom);
  // State, not a ref: finishing a page must re-run the effect below.
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reveal || reveal.conversationId !== conversationId) return;
    const rendered = messages.some(
      (m) => m.metadata?.message_id === reveal.messageId
    );
    if (rendered) {
      setReveal(null);
      // Instant: the row was pages away, and a smooth scroll that far
      // outlasts the highlight.
      requestAnimationFrame(() =>
        highlightMessage(reveal.messageId, "instant")
      );
      return;
    }
    if (!hasEarlier) {
      setReveal(null);
      return;
    }
    if (!canLoad || loading) return;
    setLoading(true);
    void loadEarlier().then((loaded) => {
      setLoading(false);
      if (!loaded) setReveal(null);
    });
  }, [
    loading,
    reveal,
    conversationId,
    messages,
    hasEarlier,
    canLoad,
    loadEarlier,
    setReveal,
  ]);
}

function LoadEarlierMessages({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) {
  const t = useTranslations("widget.status");
  return (
    <div className="flex justify-center">
      <Button
        variant="ghost"
        size="sm"
        onClick={onClick}
        disabled={isLoading}
        aria-busy={isLoading}
        className="gap-1.5 text-muted-foreground"
      >
        {isLoading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <ChevronUp className="size-4" aria-hidden />
        )}
        {t("loadPreviousMessages")}
      </Button>
    </div>
  );
}

const offsetIn = (viewport: HTMLElement, el: HTMLElement) =>
  el.getBoundingClientRect().top - viewport.getBoundingClientRect().top;

const messageElement = (viewport: HTMLElement, id: string) =>
  viewport.querySelector<HTMLElement>(
    `[data-chat-message="${CSS.escape(id)}"]`
  );
